import React, { useState, useEffect } from 'react';
import { communicationsService } from '../../services';
import { useResponsive } from '../../hooks/useResponsive';
import travelieTheme from '../../styles/travelieTheme';

// Week 1: QuoteMessagesModal TravelieTheme Migration - June 22, 2026

const QuoteMessagesModal = ({ quote, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    if (isOpen && quote?.id) {
      fetchMessages();
    }
  }, [isOpen, quote?.id]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await communicationsService.getQuoteMessages(quote.id);
      setMessages(response.messages || []);
    } catch (error) {
      console.error('Error fetching quote messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    try {
      setSendingReply(true);
      const response = await communicationsService.replyToQuoteMessage(quote.id, replyMessage.trim());
      
      // Show email status to user
      if (response.emailSent) {
        console.log('✅ Email notification sent to client');
      } else if (response.emailError) {
        console.warn('⚠️ Email notification failed:', response.emailError);
        setError(`Reply sent but email notification failed: ${response.emailError}`);
      }
      
      setReplyMessage('');
      await fetchMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending reply:', error);
      setError('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageIcon = (direction) => {
    return direction === 'inbound' ? '📥' : '📤';
  };

  const getMessageStyle = (direction) => {
    return direction === 'inbound' 
      ? {
          background: '#EFF6FF',
          borderLeft: `4px solid ${travelieTheme.colors.primary.main}`,
          padding: travelieTheme.spacing.lg,
          borderRadius: travelieTheme.components.card.borderRadius
        }
      : {
          background: travelieTheme.colors.status.successLight,
          borderLeft: `4px solid ${travelieTheme.colors.status.success}`,
          padding: travelieTheme.spacing.lg,
          borderRadius: travelieTheme.components.card.borderRadius
        };
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: travelieTheme.spacing.lg
    }}>
      <div style={{
        background: travelieTheme.colors.neutral.card,
        borderRadius: travelieTheme.components.card.borderRadius,
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: travelieTheme.spacing['2xl'],
          borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.lg,
            fontWeight: travelieTheme.typography.fontWeight.medium,
            color: travelieTheme.colors.text.primary,
            margin: 0
          }}>
            Quote Messages - {quote?.quote_number}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: travelieTheme.colors.text.secondary,
              padding: 0
            }}
          >
            <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div style={{
          padding: travelieTheme.spacing['2xl'],
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Quote Info */}
          <div style={{
            background: travelieTheme.colors.neutral.background,
            padding: travelieTheme.spacing.lg,
            borderRadius: travelieTheme.components.card.borderRadius,
            marginBottom: travelieTheme.spacing.lg
          }}>
            <h4 style={{
              fontWeight: travelieTheme.typography.fontWeight.medium,
              color: travelieTheme.colors.text.primary,
              marginBottom: travelieTheme.spacing.xs
            }}>{quote?.title || 'Travel Quote'}</h4>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: travelieTheme.spacing.xs
            }}>Client: {quote?.client_name}</p>
            {quote?.total_amount && (
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                margin: 0
              }}>
                Total: ${parseFloat(quote.total_amount).toLocaleString()}
              </p>
            )}
          </div>

          {/* Messages */}
          <div>
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: `${travelieTheme.spacing['2xl']} 0`
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: `2px solid ${travelieTheme.colors.primary.main}`,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  margin: '0 auto',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                  marginTop: travelieTheme.spacing.sm,
                  color: travelieTheme.colors.text.secondary
                }}>Loading messages...</p>
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: `${travelieTheme.spacing['2xl']} 0`,
                color: '#DC2626'
              }}>
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: `${travelieTheme.spacing['2xl']} 0`,
                color: travelieTheme.colors.text.muted
              }}>
                No messages found for this quote.
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: travelieTheme.spacing.md,
                maxHeight: '384px',
                overflowY: 'auto'
              }}>
                {messages.map((message) => (
                  <div key={message.id} style={getMessageStyle(message.direction)}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: travelieTheme.spacing.sm
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.sm
                      }}>
                        <span style={{ fontSize: travelieTheme.typography.fontSize.lg }}>{getMessageIcon(message.direction)}</span>
                        <span style={{
                          fontWeight: travelieTheme.typography.fontWeight.medium,
                          fontSize: travelieTheme.typography.fontSize.sm,
                          color: travelieTheme.colors.text.primary
                        }}>
                          {message.direction === 'inbound' ? 'Client' : 'Agent'}
                        </span>
                      </div>
                      <span style={{
                        fontSize: travelieTheme.typography.fontSize.xs,
                        color: travelieTheme.colors.text.muted
                      }}>
                        {formatDate(message.communication_date)}
                      </span>
                    </div>
                    <p style={{
                      color: travelieTheme.colors.text.primary,
                      margin: 0,
                      lineHeight: '1.5'
                    }}>{message.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reply Form */}
          <form onSubmit={handleSendReply} style={{ marginTop: travelieTheme.spacing['2xl'] }}>
            <div style={{ marginBottom: travelieTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.primary,
                marginBottom: travelieTheme.spacing.sm
              }}>
                Send Reply to Client
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                style={{
                  width: '100%',
                  padding: travelieTheme.spacing.sm,
                  border: `1px solid ${travelieTheme.colors.neutral.border}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  fontSize: travelieTheme.typography.fontSize.sm,
                  outline: 'none',
                  resize: 'vertical'
                }}
                rows={4}
                placeholder="Type your reply message here..."
                disabled={sendingReply}
                onFocus={(e) => e.target.style.borderColor = travelieTheme.colors.primary.main}
                onBlur={(e) => e.target.style.borderColor = travelieTheme.colors.neutral.border}
              />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: travelieTheme.spacing.md
            }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
                  border: `1px solid ${travelieTheme.colors.neutral.border}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  background: travelieTheme.colors.neutral.card,
                  color: travelieTheme.colors.text.primary,
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.medium,
                  cursor: sendingReply ? 'not-allowed' : 'pointer',
                  opacity: sendingReply ? 0.5 : 1,
                  transition: `background ${travelieTheme.transitions.fast}`
                }}
                disabled={sendingReply}
                onMouseEnter={(e) => !sendingReply && (e.target.style.background = travelieTheme.colors.neutral.hover)}
                onMouseLeave={(e) => e.target.style.background = travelieTheme.colors.neutral.card}
              >
                Close
              </button>
              <button
                type="submit"
                disabled={!replyMessage.trim() || sendingReply}
                style={{
                  padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
                  border: 'none',
                  borderRadius: travelieTheme.components.card.borderRadius,
                  background: travelieTheme.colors.primary.main,
                  color: '#FFFFFF',
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.medium,
                  cursor: (!replyMessage.trim() || sendingReply) ? 'not-allowed' : 'pointer',
                  opacity: (!replyMessage.trim() || sendingReply) ? 0.5 : 1,
                  transition: `background ${travelieTheme.transitions.fast}`
                }}
                onMouseEnter={(e) => (!replyMessage.trim() || sendingReply) ? null : (e.target.style.background = travelieTheme.colors.primary.hover)}
                onMouseLeave={(e) => e.target.style.background = travelieTheme.colors.primary.main}
              >
                {sendingReply ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuoteMessagesModal;
